import * as React from 'react';
import { ChevronDown, FileText } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import { PageCarousel, type CarouselPage } from '@/components/ui/page-carousel';
import { Spinner } from '@/components/ui/spinner';
import { StatusChip } from '@/components/ui/status-chip';
import { WorkspaceButton, WorkspaceMenu } from '@/components/ui/workspace';
import { PdfPreviewModal } from '@/components/modals/pdf-preview-modal';
import type { EditorCitation } from '@/components/report-editor';
import {
  usePublishedReportQuery,
  type ReportCitation,
  type ReportVersion,
  type VendorReport,
} from '@/lib/api/vendor-intelligence-report';
import { formatDate } from '@/lib/utils/format';
import { EmptyReport } from './empty-report';
import { PDF_STATE_LABEL, pdfState, plural } from './report-logic';
import { carouselPages, modelFromPublished, type ReportModel } from './report-model';
import { ReportPageView, type ReportPageEditing } from './report-page';
import { byLine, versionsNewestFirst, type PrepareState } from './report-views';

const NO_EDITING = {
  onRewrite: () => Promise.reject(new Error('read-only')),
  onSave: () => Promise.reject(new Error('read-only')),
  onRevert: () => Promise.reject(new Error('read-only')),
};

/**
 * The step as it opens: the latest published version, read-only, in the
 * carousel, with one line above it (the version, when and by whom, Open
 * PDF, and for a consultant the way into the draft). With two or more
 * versions the chip is a menu. Before anything is published, the one box
 * of `EmptyReport`.
 */
export function PublishedView({
  engagementId,
  report,
  engagement,
  prepare,
  onOpenDraft,
  onOpenCitation,
}: {
  engagementId: string;
  report: VendorReport;
  engagement?: { client_name: string; name: string } | null;
  prepare: PrepareState;
  onOpenDraft: () => void;
  onOpenCitation: (citation: ReportCitation | null) => void;
}) {
  const versions = React.useMemo(() => versionsNewestFirst(report), [report]);
  const latest = versions[0] ?? null;
  // Null until a version is chosen, so the view follows the newest one as it lands.
  const [chosenId, setChosenId] = React.useState<string | null>(null);
  const shown: ReportVersion | null = versions.find((v) => v.snapshot_id === chosenId) ?? latest;
  const published = usePublishedReportQuery(engagementId, shown?.snapshot_id ?? null);
  const model: ReportModel | null = React.useMemo(
    () => (published.data && shown ? modelFromPublished(published.data, shown.version) : null),
    [published.data, shown],
  );
  const [current, setCurrent] = React.useState<string | undefined>(undefined);
  const [pdfVersion, setPdfVersion] = React.useState<ReportVersion | null>(null);

  // The strip without the draft's dots and counts: a published page has no issues left to show.
  const pages: CarouselPage[] = React.useMemo(
    () => (model ? carouselPages(model).map(({ key, title }) => ({ key, title })) : []),
    [model],
  );
  const currentKey = current && pages.some((p) => p.key === current) ? current : pages[0]?.key;

  const modelRef = React.useRef(model);
  modelRef.current = model;
  const editing: ReportPageEditing = React.useMemo(
    () => ({
      readOnly: true,
      ...NO_EDITING,
      onOpenCitation: (c: EditorCitation) =>
        onOpenCitation(modelRef.current?.citations.get(c.id) ?? null),
    }),
    [onOpenCitation],
  );

  if (!shown) {
    return <EmptyReport report={report} prepare={prepare} onOpenDraft={onOpenDraft} />;
  }

  const state = pdfState(shown);
  const draft = report.draft;
  const header = (
    <div className="flex flex-col gap-2" data-testid="vi-report-published-bar">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {versions.length > 1 ? (
          <WorkspaceMenu
            aria-label="Versions"
            label="Published versions"
            align="start"
            trigger={
              <WorkspaceButton variant="tertiary" className="-ml-2.5">
                <StatusChip tone="frozen" size="sm">
                  Published · version {shown.version}
                </StatusChip>
                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              </WorkspaceButton>
            }
            items={versions.map((v) => ({
              id: v.snapshot_id,
              label: `Version ${v.version}`,
              hint:
                v.snapshot_id === shown.snapshot_id
                  ? `${byLine(formatDate(v.published_at), v.published_by.name)} · shown`
                  : byLine(formatDate(v.published_at), v.published_by.name),
              onSelect: () => {
                setChosenId(v.snapshot_id);
                setCurrent(undefined);
              },
            }))}
          />
        ) : (
          <StatusChip tone="frozen" size="sm">
            Published · version {shown.version}
          </StatusChip>
        )}
        <span className="text-meta text-ink-500">
          {byLine(formatDate(shown.published_at), shown.published_by.name)}
          {pages.length > 0 ? ` · ${plural(pages.length, 'page')}` : ''}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {state !== 'ready' ? (
            <StatusChip tone={state === 'failed' ? 'critical' : 'ai'} size="sm">
              {PDF_STATE_LABEL[state]}
            </StatusChip>
          ) : null}
          {report.can_edit ? (
            <WorkspaceButton variant="tertiary" onClick={onOpenDraft}>
              {draft ? 'Edit draft' : 'New draft'}
            </WorkspaceButton>
          ) : null}
          <WorkspaceButton variant="primary" onClick={() => setPdfVersion(shown)}>
            <FileText className="h-3.5 w-3.5" aria-hidden="true" />
            Open PDF
          </WorkspaceButton>
        </div>
      </div>
      {draft && report.can_edit ? (
        <p
          className="m-0 border-l-2 border-accent-amber-600 pl-2.5 text-meta text-ink-700"
          data-testid="vi-report-draft-line"
        >
          Draft · version {draft.version} in progress, prepared{' '}
          {byLine(formatDate(draft.drafted_at), draft.drafted_by.name)}.{' '}
          <button
            type="button"
            onClick={onOpenDraft}
            className="font-semibold text-ink-900 underline-offset-2 hover:underline"
          >
            Open draft
          </button>
        </p>
      ) : null}
      {published.error ? <ErrorMessage error={published.error} /> : null}
    </div>
  );

  return (
    <>
      {model && pages.length > 0 ? (
        <PageCarousel
          aria-label="Report pages"
          header={header}
          pages={pages}
          current={currentKey}
          onChange={(key) => setCurrent(key)}
          renderPage={(page, index) => {
            const pageModel = model.pages.find((p) => p.key === page.key);
            return pageModel ? (
              <ReportPageView
                model={model}
                page={pageModel}
                index={index}
                count={pages.length}
                editing={editing}
                engagement={engagement}
              />
            ) : null;
          }}
          className="min-h-0 flex-1"
        />
      ) : (
        <>
          {header}
          {published.isLoading ? (
            <p className="m-0 flex items-center gap-2 text-body-sm text-ink-500">
              <Spinner className="h-3.5 w-3.5" /> Loading version {shown.version}…
            </p>
          ) : null}
        </>
      )}
      {pdfVersion ? (
        <PdfPreviewModal
          engagementId={engagementId}
          snapshotId={pdfVersion.snapshot_id}
          snapshotLabel={`Vendor intelligence report · version ${pdfVersion.version}`}
          open
          onOpenChange={(open) => {
            if (!open) setPdfVersion(null);
          }}
        />
      ) : null}
    </>
  );
}
